import { isTruthy } from '@app/utils/isTruthy'

import type { ApiPlace, ApiRegion } from '@app/packages/api/v2/types/entities'
import type { ApiDataResponse } from '@app/packages/api/v2/types/misc'

import { countriesSelector, localitiesSelector, placesSelector } from '@app/store/slices/entities'
import { regionsMapSelector } from '@app/store/slices/regions'
import { createThunk } from '@app/store/thunk'

import { callV2Api } from './api'
import { fetchRegion } from './initial'

export function getPlaceById(id: string) {
  return createThunk<Promise<ApiDataResponse<ApiPlace>>>(async dispatch => {
    const cached = dispatch(getPlaceResponse(id))
    if (cached) return cached

    const { getPlacesById } = await import('@app/packages/api/v2/actions/places')
    return await dispatch(callV2Api(getPlacesById(id)))
  })
}

export function getPlaceByRegion(regionId: string) {
  return createThunk<Promise<ApiDataResponse<ApiPlace>>>(async (dispatch, getState) => {
    const region = regionsMapSelector(getState())[regionId]
    if (region) {
      const id = region.relationships.default_place.data?.id
      if (id) {
        const resp = dispatch(getPlaceResponse(id))
        if (resp) return resp
      }
    }

    await dispatch(fetchRegion(regionId))

    const defaultPlaceData = regionsMapSelector(getState())[regionId]?.relationships.default_place.data
    if (!defaultPlaceData) throw new Error("Region'sdefault place missing")
    const id = defaultPlaceData.id

    const place = dispatch(getPlaceResponse(id))
    if (!place) throw new Error('Place not found')
    return place
  })
}

/** The region of a coordinate, from the place the api resolves it to. */
export function getRegionByLocation(latitude: number, longitude: number) {
  return createThunk(async dispatch => {
    const { getPlacesByLocation } = await import('@app/packages/api/v2/actions/places')
    const resp = await dispatch(callV2Api(getPlacesByLocation({ latitude, longitude })))
    const region = resp.included.find(en => en.type === 'regions') as ApiRegion

    return region
  })
}

function getPlaceResponse(placeId: string) {
  return createThunk((_dispatch, getState): ApiDataResponse<ApiPlace> | null => {
    const state = getState()
    const place = placesSelector(state)[placeId]
    if (!place) return null

    const region = regionsMapSelector(state)[place.relationships.region.data!.id]
    const country = (place.relationships.country?.data?.id && countriesSelector(state)[place.relationships.country.data.id]) || null
    const locality = (place.relationships.locality?.data?.id && localitiesSelector(state)[place.relationships.locality.data.id]) || null

    return { data: place, included: [region, country, locality].filter(isTruthy) }
  })
}
